import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ticketGet } from '../../services/apiClient';
import { userManagementService } from '../../api/userManagementService';
import { extractErrorMessage } from '../../utils/errorHandler';
import { excludeSuperAdminFromReportRows } from '../../utils/reportFilters';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import CountyFilterSelect, { countyFilterToId, regionFilterToId } from '../../components/reports/CountyFilterSelect';
import { 
  AlertTriangle, 
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  BarChart3,
  Download,
  ChevronDown,
  FileText,
  Filter
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { downloadStyledXlsx } from '../../utils/exportStyledXlsx';

const CHART_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

const AgentWiseSLABreachReport = () => {
  const navigate = useNavigate();
  const [slaData, setSlaData] = useState([]);
  const [filteredSlaData, setFilteredSlaData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Filter states
  const [selectedAgent, setSelectedAgent] = useState('all');
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [selectedCounty, setSelectedCounty] = useState('all');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);
  const [paginationData, setPaginationData] = useState(null);
  
  const [exporting, setExporting] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  // Dropdown data
  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);

  // Load agents on mount
  useEffect(() => {
    loadAgents();
  }, []);

  // Load report when filters change (filtering applied by backend)
  useEffect(() => {
    loadSLAReport();
  }, [selectedAgent, selectedRegion, selectedCounty]);

  // Paginate already backend-filtered data for the table only
  useEffect(() => {
    applyPagination(slaData);
  }, [slaData, currentPage, pageSize]);

  const loadAgents = async () => {
    try {
      setLoadingAgents(true);
      const response = await userManagementService.getAgents({ size: 1000 }); // Get all agents
      const agentsData = response?.data?.data?.content || response?.data?.data || response?.data || [];
      const agentsArray = Array.isArray(agentsData) ? agentsData : [];
      setAgents(agentsArray);
    } catch (error) {
      console.warn('Failed to load agents:', error);
      setAgents([]);
    } finally {
      setLoadingAgents(false);
    }
  };

  const loadSLAReport = async () => {
    try {
      setLoading(true);
      setError('');
      
      const params = {
        type: 'SLA_COMPLIANCE'
      };

      if (selectedAgent !== 'all') {
        const agentId = parseInt(selectedAgent, 10);
        if (!isNaN(agentId)) params.agentId = agentId;
      }
      const regionId = regionFilterToId(selectedRegion);
      if (regionId !== null) params.regionId = regionId;
      const countyId = countyFilterToId(selectedCounty);
      if (countyId !== null) params.countyId = countyId;

      const response = await ticketGet('/gateway/api/ticket/analytics/report', params);
      
      let reportData = [];
      
      if (response?.success && response?.data) {
        reportData = Array.isArray(response.data) ? response.data : [];
      } else if (response?.data?.success && response?.data?.data) {
        reportData = Array.isArray(response.data.data) ? response.data.data : [];
      } else if (response?.data) {
        reportData = Array.isArray(response.data) ? response.data : [];
      } else if (Array.isArray(response)) {
        reportData = response;
      }
      
      reportData = reportData.map(item => ({
        ...item,
        assigneeName: item.assigneeName || item.key || 'Unknown Agent',
        assigneeId: item.assigneeId || item.userId || item.id
      }));

      reportData = excludeSuperAdminFromReportRows(reportData);
      setSlaData(reportData);
    } catch (error) {
      console.error('Error loading SLA breach report:', error);
      setError(extractErrorMessage(error, 'Failed to load SLA breach report'));
      toast.error(extractErrorMessage(error, 'Failed to load SLA breach report'));
      setSlaData([]);
      setFilteredSlaData([]);
      setPaginationData(null);
    } finally {
      setLoading(false);
    }
  };

  const applyPagination = (dataToPaginate) => {
    if (!dataToPaginate || dataToPaginate.length === 0) {
      setFilteredSlaData([]);
      setPaginationData({
        totalElements: 0,
        totalPages: 0,
        pageNumber: currentPage,
        pageSize: pageSize,
        first: true,
        last: true
      });
      return;
    }
    
    const start = currentPage * pageSize;
    const end = start + pageSize;
    const paginatedData = dataToPaginate.slice(start, end);
    
    setFilteredSlaData(paginatedData);
    setPaginationData({
      totalElements: dataToPaginate.length,
      totalPages: Math.ceil(dataToPaginate.length / pageSize),
      pageNumber: currentPage,
      pageSize: pageSize,
      first: currentPage === 0,
      last: end >= dataToPaginate.length
    });
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && (paginationData ? newPage < paginationData.totalPages : true)) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleClearFilters = () => {
    setSelectedAgent('all');
    setSelectedRegion('all');
    setSelectedCounty('all');
    setCurrentPage(0);
  };

  const formatPercentage = (value) => {
    if (value === null || value === undefined) return '0.00';
    return typeof value === 'number' ? value.toFixed(2) : parseFloat(value).toFixed(2);
  };

  const hasActiveFilters = selectedAgent !== 'all' || selectedRegion !== 'all' || selectedCounty !== 'all';

  // Charts use full backend-filtered response (not table page slice)
  const chartSourceData = slaData || [];

  const histogramChartData = useMemo(
    () =>
      chartSourceData
        .map((item) => ({
          name: (item.assigneeName || item.key || 'Unknown').substring(0, 20),
          count: Number(item.totalCount) || 0
        }))
        .filter((d) => d.count > 0),
    [chartSourceData]
  );

  const barChartData = useMemo(
    () =>
      chartSourceData.map((item) => ({
        name: (item.assigneeName || item.key || 'Unknown').substring(0, 12),
        total: Number(item.totalCount) || 0,
        slaMet: Number(item.slaMetCount) || 0,
        slaBreached: Number(item.slaBreachedCount) || 0,
        compliancePct: Number(item.slaCompliancePercentage) || 0
      })),
    [chartSourceData]
  );

  const handleExportExcel = async () => {
    setExportMenuOpen(false);
    setExporting(true);
    try {
      const exportData = slaData || [];
      if (exportData.length === 0) {
        toast.error('No data to export');
        return;
      }
      const headers = [
        'S.No',
        'Assignee ID',
        'Agent Name',
        'Total Tickets',
        'SLA Met',
        'SLA Breached',
        'SLA Compliance %',
      ];
      const rows = exportData.map((r, i) => [
        i + 1,
        r.assigneeId != null ? String(r.assigneeId) : '',
        r.assigneeName || r.key || 'Unknown Agent',
        r.totalCount ?? 0,
        r.slaMetCount ?? 0,
        r.slaBreachedCount ?? 0,
        formatPercentage(r.slaCompliancePercentage),
      ]);
      downloadStyledXlsx({
        fileName: `agent-wise-sla-breach-report-${new Date().toISOString().slice(0, 10)}.xlsx`,
        sheetName: 'Agent Wise SLA Breach',
        headers,
        rows,
        numberColumns: [0, 3, 4, 5],
        textColumns: [1, 2, 6],
        centerColumns: [0, 1, 3, 4, 5, 6],
      });
      toast.success('Report exported as Excel');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    setExportMenuOpen(false);
    setExporting(true);
    try {
      const exportData = slaData || [];
      if (exportData.length === 0) {
        toast.error('No data to export');
        return;
      }
      const doc = new jsPDF({ orientation: 'landscape' });
      doc.setFontSize(14);
      doc.text('Agent wise SLA Breach Report', 14, 15);
      doc.setFontSize(10);
      const head = [[
        'S.No',
        'Assignee ID',
        'Agent Name',
        'Total',
        'SLA Met',
        'SLA Breached',
        'Compliance %'
      ]];
      const body = exportData.map((r, i) => [
        i + 1,
        r.assigneeId ?? '',
        (r.assigneeName || r.key || 'Unknown Agent').substring(0, 30),
        r.totalCount ?? 0,
        r.slaMetCount ?? 0,
        r.slaBreachedCount ?? 0,
        formatPercentage(r.slaCompliancePercentage)
      ]);
      autoTable(doc, { startY: 22, head, body, styles: { fontSize: 9 } });
      doc.save(`agent-wise-sla-breach-report-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success('Report exported as PDF');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Agent wise SLA Breach Report</h1>
          <p className="text-sm text-muted-foreground mt-1">
            View SLA compliance statistics for each agent
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setShowFilters((prev) => !prev);
              setExportMenuOpen(false);
            }}
            className="flex items-center gap-2"
            aria-expanded={showFilters}
          >
            <Filter className="h-4 w-4" />
            Filter
            {hasActiveFilters && !showFilters && (
              <span className="ml-1 h-2 w-2 rounded-full bg-primary" aria-hidden />
            )}
          </Button>
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setExportMenuOpen((v) => !v)}
              disabled={loading || exporting}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              {exporting ? 'Exporting…' : 'Export'}
              <ChevronDown className="h-4 w-4 ml-1" />
            </Button>
            {exportMenuOpen && (
              <div className="absolute right-0 top-full mt-1 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-10 min-w-[140px]">
                <button type="button" className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2" onClick={handleExportExcel}>
                  <FileText className="h-4 w-4" /> Export Excel
                </button>
                <button type="button" className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2" onClick={handleExportPdf}>
                  <FileText className="h-4 w-4" /> Export PDF
                </button>
              </div>
            )}
          </div>
          <Button
            onClick={loadSLAReport}
            disabled={loading}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters Card (toggle via Filter button) */}
      {showFilters && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:gap-4">
            <div className="grid min-w-0 flex-1 grid-cols-1 gap-4 sm:grid-cols-3">
              {/* Agent Filter */}
              <div className="min-w-0">
                <label className="mb-2 block text-sm font-medium text-foreground">
                  Agent
                </label>
                <Select
                  value={selectedAgent}
                  onValueChange={(value) => {
                    setSelectedAgent(value);
                    setCurrentPage(0);
                  }}
                  disabled={loadingAgents}
                >
                  <SelectTrigger className="h-10 w-full">
                    <SelectValue placeholder="All Agents" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Agents</SelectItem>
                    {agents.map((agent) => (
                      <SelectItem
                        key={agent.userId || agent.id}
                        value={String(agent.userId || agent.id)}
                      >
                        {agent.fullName || agent.name || agent.username || 'Unknown'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Region / County — equal share of remaining row (2 cols) */}
              <div className="min-w-0 sm:col-span-2">
                <CountyFilterSelect
                  className="!flex-col !gap-4 sm:!flex-row [&>div]:min-w-0 [&>div]:flex-1 [&_label]:mb-2 [&_label]:text-foreground dark:[&_label]:text-foreground [&_select]:h-10 [&_select]:rounded-md [&_select]:py-0"
                  regionValue={selectedRegion}
                  countyValue={selectedCounty}
                  onRegionChange={(value) => {
                    setSelectedRegion(value);
                    setCurrentPage(0);
                  }}
                  onCountyChange={(value) => {
                    setSelectedCounty(value);
                    setCurrentPage(0);
                  }}
                />
              </div>
            </div>

            {/* Clear Filters — far right, does not share dropdown column widths */}
            {hasActiveFilters && (
              <div className="flex shrink-0 items-end lg:ml-auto">
                <Button
                  variant="outline"
                  onClick={handleClearFilters}
                  className="flex h-10 items-center gap-2 whitespace-nowrap"
                >
                  Clear Filters
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      )}

      {/* Graphical representation: charts from same data as table (respects Agent filter) */}
      {!loading && chartSourceData.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <BarChart3 className="h-5 w-5" />
                Tickets by agent (histogram)
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Total tickets per agent
              </p>
            </CardHeader>
            <CardContent>
              {histogramChartData.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">No data to display.</p>
              ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart
                    data={histogramChartData}
                    margin={{ top: 8, right: 8, left: 8, bottom: 24 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="name" className="text-xs" tick={{ fontSize: 10 }} />
                    <YAxis allowDecimals={false} className="text-xs" />
                    <Tooltip
                      formatter={(value) => [value, 'Tickets']}
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                      labelFormatter={(name) => name}
                    />
                    <Bar dataKey="count" fill={CHART_COLORS[0]} name="Tickets" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <BarChart3 className="h-5 w-5" />
                SLA Met vs Breached by agent
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Green = SLA Met, Red = SLA Breached
              </p>
            </CardHeader>
            <CardContent>
              {barChartData.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">No data to display.</p>
              ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart
                    data={barChartData}
                    margin={{ top: 8, right: 8, left: 8, bottom: 24 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="name" className="text-xs" tick={{ fontSize: 10 }} />
                    <YAxis allowDecimals={false} className="text-xs" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                      formatter={(value) => [value]}
                    />
                    <Legend wrapperStyle={{ fontSize: '11px' }} />
                    <Bar dataKey="slaMet" stackId="sla" fill="#10B981" name="SLA Met" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="slaBreached" stackId="sla" fill="#EF4444" name="SLA Breached" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* SLA Breach Report Table Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            SLA Compliance Statistics
            {paginationData && (
              <span className="text-sm font-normal text-muted-foreground ml-2">
                ({paginationData.totalElements} total)
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-800 text-sm">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Loading SLA breach report...</span>
            </div>
          ) : filteredSlaData.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <AlertTriangle className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No SLA data found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-border bg-muted/50">
                      <th className="text-left p-3 text-sm font-semibold text-foreground">Agent Name</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground">Total Tickets</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground">SLA Breached</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground">SLA Met</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground">SLA Compliance %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSlaData.map((slaDataItem, index) => (
                      <tr
                        key={slaDataItem.assigneeId || slaDataItem.userId || slaDataItem.id || index}
                        className="border-b border-border hover:bg-muted/30 transition-colors"
                      >
                        <td className="p-3 text-sm text-foreground font-medium">
                          {slaDataItem.assigneeName || slaDataItem.key || 'Unknown Agent'}
                        </td>
                        <td className="p-3 text-sm text-foreground">
                          {slaDataItem.totalCount || 0}
                        </td>
                        <td className="p-3 text-sm text-foreground">
                          <span className="text-red-600 font-medium">
                            {slaDataItem.slaBreachedCount || 0}
                          </span>
                        </td>
                        <td className="p-3 text-sm text-foreground">
                          <span className="text-green-600 font-medium">
                            {slaDataItem.slaMetCount || 0}
                          </span>
                        </td>
                        <td className="p-3 text-sm text-foreground">
                          <span className={`font-medium ${
                            (slaDataItem.slaCompliancePercentage || 0) >= 90 ? 'text-green-600' :
                            (slaDataItem.slaCompliancePercentage || 0) >= 70 ? 'text-yellow-600' :
                            'text-red-600'
                          }`}>
                            {formatPercentage(slaDataItem.slaCompliancePercentage)}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {paginationData && paginationData.totalElements > 0 && (
                <div className="flex flex-col sm:flex-row items-center justify-between mt-6 pt-4 border-t border-border gap-4">
                  <div className="text-sm text-muted-foreground">
                    Showing {currentPage * pageSize + 1} to{' '}
                    {Math.min((currentPage + 1) * pageSize, paginationData.totalElements)} of{' '}
                    {paginationData.totalElements} agents
                  </div>
                  <div className="flex items-center gap-2">
                    {/* First Page Button */}
                    {paginationData.totalPages > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(0)}
                        disabled={currentPage === 0 || loading}
                        className="hidden sm:flex"
                      >
                        First
                      </Button>
                    )}
                    
                    {/* Previous Button */}
                    {paginationData.totalPages > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 0 || loading}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Previous
                      </Button>
                    )}
                    
                    {/* Page Numbers */}
                    {paginationData.totalPages > 0 && (
                      <div className="flex items-center gap-1">
                        {(() => {
                          const pages = [];
                          const totalPages = paginationData.totalPages;
                          const current = currentPage + 1;
                          
                          if (totalPages <= 7) {
                            for (let i = 1; i <= totalPages; i++) {
                              pages.push(
                                <Button
                                  key={i}
                                  variant={i === current ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => handlePageChange(i - 1)}
                                  disabled={loading || totalPages === 1}
                                  className="min-w-[2.5rem]"
                                >
                                  {i}
                                </Button>
                              );
                            }
                          } else {
                            pages.push(
                              <Button
                                key={1}
                                variant={current === 1 ? "default" : "outline"}
                                size="sm"
                                onClick={() => handlePageChange(0)}
                                disabled={loading}
                                className="min-w-[2.5rem]"
                              >
                                1
                              </Button>
                            );
                            
                            if (current > 3) {
                              pages.push(
                                <span key="ellipsis-start" className="px-2 text-muted-foreground">
                                  ...
                                </span>
                              );
                            }
                            
                            const start = Math.max(2, current - 1);
                            const end = Math.min(totalPages - 1, current + 1);
                            
                            for (let i = start; i <= end; i++) {
                              if (i !== 1 && i !== totalPages) {
                                pages.push(
                                  <Button
                                    key={i}
                                    variant={i === current ? "default" : "outline"}
                                    size="sm"
                                    onClick={() => handlePageChange(i - 1)}
                                    disabled={loading}
                                    className="min-w-[2.5rem]"
                                  >
                                    {i}
                                  </Button>
                                );
                              }
                            }
                            
                            if (current < totalPages - 2) {
                              pages.push(
                                <span key="ellipsis-end" className="px-2 text-muted-foreground">
                                  ...
                                </span>
                              );
                            }
                            
                            pages.push(
                              <Button
                                key={totalPages}
                                variant={current === totalPages ? "default" : "outline"}
                                size="sm"
                                onClick={() => handlePageChange(totalPages - 1)}
                                disabled={loading}
                                className="min-w-[2.5rem]"
                              >
                                {totalPages}
                              </Button>
                            );
                          }
                          
                          return pages;
                        })()}
                      </div>
                    )}
                    
                    {/* Next Button */}
                    {paginationData.totalPages > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage >= paginationData.totalPages - 1 || loading}
                      >
                        Next
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    )}
                    
                    {/* Last Page Button */}
                    {paginationData.totalPages > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(paginationData.totalPages - 1)}
                        disabled={currentPage >= paginationData.totalPages - 1 || loading}
                        className="hidden sm:flex"
                      >
                        Last
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AgentWiseSLABreachReport;
